
import { useState } from "react";
import { useProject } from "@/hooks/useProject";
import { useJotFormSubmissions } from "@/hooks/useJotFormSubmissions";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface SubmissionSearchSelectProps {
  onSelect: (submission: any) => void;
  jotformFormId: string | null;
}

export function SubmissionSearchSelect({ onSelect, jotformFormId }: SubmissionSearchSelectProps) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string>("");
  const { projectId } = useProject();

  // Get submissions from JotForm synced table (optimized for preview)
  const { data, isLoading, sync, isSyncing } = useJotFormSubmissions({
    formId: jotformFormId || undefined,
    enabled: !!jotformFormId && !!projectId,
    optimized: true, // Usar consulta optimizada para vista previa
  });

  // Helper to get email from answers
  const getSubmissionEmail = (submission: any) => {
    if (!submission.answers) return 'N/A';

    const emailAnswer = Object.values(submission.answers).find((answer: any) => {
      const name = answer?.name?.toLowerCase() || '';
      const text = answer?.text?.toLowerCase() || '';
      return name === 'email' || name === 'correo' || name === 'referido-email' ||
             name.includes('correoelectronico') || name.includes('correo-electronico') ||
             text.includes('correo electrónico');
    });

    return (emailAnswer as any)?.answer || 'N/A';
  };

  // Helper to get name from answers
  const getSubmissionName = (submission: any) => {
    if (!submission.answers) return 'N/A';

    let firstName = '';
    let lastName = '';

    Object.values(submission.answers).forEach((answer: any) => {
      const name = answer?.name?.toLowerCase() || '';
      if (name === 'nombre' || name === 'referido-nombre') {
        firstName = answer.answer || answer.text || '';
      }
      if (name === 'apellido' || name === 'referido-apellido2') {
        lastName = answer.answer || answer.text || '';
      }
    });

    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    return 'N/A';
  };

  // Filter submissions by search
  const filteredSubmissions = data?.submissions?.filter(submission => {
    if (!search) return true;
    const email = getSubmissionEmail(submission).toLowerCase();
    const name = getSubmissionName(submission).toLowerCase();
    const searchLower = search.toLowerCase();
    return email.includes(searchLower) ||
           name.includes(searchLower) ||
           submission.id.includes(searchLower) ||
           (submission.code && submission.code.toLowerCase().includes(searchLower));
  }) || [];

  if (!jotformFormId) {
    return (
      <div className="text-sm text-muted-foreground">
        No hay un formulario JotForm configurado para esta plantilla.
        Por favor, edita la plantilla y selecciona un formulario.
      </div>
    );
  }

  return (
    <div className="flex gap-2 items-center">
      <Input
        type="text"
        placeholder="Buscar por nombre, email o código"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-60"
      />
      <Select
        value={selectedId}
        onValueChange={(val) => {
          setSelectedId(val);
          const found = filteredSubmissions?.find((s: any) => s.id === val);
          if (found) onSelect(found);
        }}
        disabled={isLoading || !filteredSubmissions?.length}
      >
        <SelectTrigger className="w-72">
          <SelectValue placeholder={isLoading ? "Cargando..." : "Seleccionar submission..."}>
            {selectedId ? getSubmissionEmail(filteredSubmissions?.find((s: any) => s.id === selectedId)) : null}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {filteredSubmissions && filteredSubmissions.map((s: any) => (
            <SelectItem key={s.id} value={s.id}>
              {getSubmissionEmail(s)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        onClick={() => sync && sync()}
        variant="outline"
        size="sm"
        disabled={isSyncing}
      >
        <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
      </Button>
    </div>
  );
}
